import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../components/icons.jsx";
import { Eyebrow, NFCCard, PhotoFrame, FairCommitment, Editorial } from "../components/ui.jsx";
import { useLocaleNav } from "../lib/useLocaleNav.js";

export default function Home({ tweaks }) {
  const go = useLocaleNav();
  return (
    <main>
      <Hero go={go} tweaks={tweaks} />
      <PhotoStrip />
      <ProofStrip />
      <Mechanism go={go} />
      <CaseRail go={go} />
      <FairCommitment />
      <FullBleedPhoto />
      <PricingTeaser go={go} />
      <CTA go={go} />
    </main>
  );
}

function PhotoStrip() {
  const shots = [
    { caption: "Server hands the NFC card to a guest, end of service.", hint: "Bistrot Mărgărit · Bucharest", tone: "ink", ratio: "4/5" },
    { caption: "A guest taps her phone to the card, two minutes before the bill.", hint: "At-the-table moment · Casa Verde", tone: "wash", ratio: "4/5" },
    { caption: "F&B director on the floor, reading a live alert.", hint: "Hanul Drumețului · Brașov", tone: "ink", ratio: "4/5" }
  ];
  return (
    <section style={{ padding: "24px 0 120px", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 18 }} className="photo-strip">
          {shots.map((s, i) => (
            <PhotoFrame key={i} ratio={s.ratio} tone={s.tone} caption={s.caption} hint={s.hint} />
          ))}
        </div>
      </div>
      <style>{`@media (max-width: 900px){.photo-strip{grid-template-columns:1fr !important}}`}</style>
    </section>
  );
}

function FullBleedPhoto() {
  return (
    <section style={{ padding: 0, borderBottom: "1px solid var(--line)" }}>
      <PhotoFrame
        ratio="21/9"
        tone="ink"
        label="On the floor"
        caption="A full dining floor at peak service — every table within reach of an NFC card."
        hint="Casa Verde, Cluj-Napoca · photographed during a Saturday dinner"
        style={{ borderRadius: 0 }}
      />
    </section>
  );
}

function Hero({ go, tweaks }) {
  const { t } = useTranslation();
  const wash = tweaks?.heroWash !== "plain";
  return (
    <section className={wash ? "bg-wash" : ""} style={{ position: "relative", overflow: "hidden" }}>
      <div className="container" style={{ padding: "48px 24px 96px", position: "relative" }}>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 0.95fr)", gap: 56, alignItems: "center" }} className="hero-grid">
          <div>
            <div className="tagline" style={{ marginBottom: 24 }}>{t("common.tagline")}</div>
            <h1 className="tt-h tt-h-xl">
              A year of reviews,<br />
              <span style={{ color: "var(--persimmon)", fontFamily: "Satoshi" }}>in two days.</span>
            </h1>
            <p style={{ margin: "32px 0 0", maxWidth: 520, fontSize: "clamp(18px, 1.4vw, 21px)", lineHeight: 1.55, color: "var(--ink-2)" }}>
              TableTalk is the guest feedback platform for restaurants. The team taps an NFC card at the table, the guest shares — and you hear from them while the table is still warm.
            </p>
            <div style={{ marginTop: 36, display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center" }}>
              <a href="#" className="btn btn-primary btn-lg" onClick={(e) => { e.preventDefault(); go("contact"); }}>
                {t("common.bookDemo")} <Icon name="arrow-right" size={16} />
              </a>
              <a href="#" className="btn btn-link" onClick={(e) => { e.preventDefault(); go("how"); }}>{t("common.seeHowItWorks")}</a>
            </div>
            <div style={{
              marginTop: 64, display: "flex", alignItems: "center", gap: 24,
              padding: "18px 24px", borderRadius: 8,
              background: "color-mix(in oklab, var(--bg) 70%, transparent)",
              border: "1px solid var(--line)", maxWidth: 520
            }}>
              <div className="tt-big" style={{ fontSize: 48, color: "var(--persimmon)", lineHeight: 0.9 }}>+50</div>
              <div>
                <div style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: 15, color: "var(--ink)" }}>Google reviews in 48 hours</div>
                <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4 }}>after a decade of operation · Casa Verde, Cluj</div>
              </div>
            </div>
          </div>
          <HeroVisual />
        </div>
        <div style={{ marginTop: 96, maxWidth: 760 }}>
          <div style={{ width: 48, height: 1, background: "var(--persimmon)", marginBottom: 20 }} />
          <p className="tt-edit" style={{ margin: 0, fontSize: "clamp(20px, 2vw, 26px)", lineHeight: 1.4, color: "var(--ink-2)" }}>
            The silent majority of guests never leave reviews. The silent unhappy minority never tell you why. We built TableTalk for both.
          </p>
        </div>
      </div>
      <style>{`@media (max-width: 980px){.hero-grid{grid-template-columns:1fr !important; gap: 56px !important}}`}</style>
    </section>
  );
}

function HeroVisual() {
  return (
    <div style={{ position: "relative", minHeight: 420, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ position: "absolute", inset: "-60px", background: "radial-gradient(circle at 60% 50%, color-mix(in oklab, var(--marigold) 40%, transparent), transparent 60%)", filter: "blur(20px)", pointerEvents: "none" }} />
      <div style={{ position: "relative", zIndex: 2, width: 268, height: 540, borderRadius: 38, padding: 12, background: "var(--ink)", boxShadow: "0 40px 80px -30px rgba(16,14,12,0.35), 0 12px 24px -12px rgba(16,14,12,0.2)" }}>
        <div style={{ width: "100%", height: "100%", borderRadius: 28, background: "var(--bg)", padding: "52px 22px 24px", display: "flex", flexDirection: "column", gap: 18, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", width: 90, height: 6, borderRadius: 99, background: "rgba(0,0,0,.06)" }} />
          <div style={{ display: "inline-flex", alignItems: "center" }}>
            <img src="/assets/TableTalk_logo.svg" alt="TableTalk" style={{ height: 14, display: "block" }} />
          </div>
          <div>
            <div style={{ fontSize: 10.5, letterSpacing: ".16em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 500 }}>Casa Verde · Table 7</div>
            <div style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 700, fontSize: 22, lineHeight: 1.1, marginTop: 8, letterSpacing: "-0.02em" }}>How was your evening?</div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} style={{
                flex: 1, aspectRatio: "1/1", borderRadius: 99,
                background: i <= 4 ? "var(--persimmon)" : "transparent",
                border: i > 4 ? "1.5px solid var(--line-2)" : "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: i <= 4 ? "var(--bg)" : "var(--muted)",
                fontSize: 11, fontWeight: 600
              }}>{i}</div>
            ))}
          </div>
          <div style={{ border: "1px solid var(--line)", borderRadius: 6, padding: "12px 14px", fontFamily: "Fraunces, serif", fontStyle: "italic", fontSize: 13, color: "var(--ink-2)", lineHeight: 1.45 }}>
            "Risotto came cold, but Alex caught it before dessert. Brilliant team."
          </div>
          <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ padding: "12px 14px", background: "var(--persimmon)", color: "var(--bg)", borderRadius: 8, fontSize: 13, fontWeight: 600, textAlign: "center" }}>Submit feedback</div>
            <div style={{ fontSize: 10.5, color: "var(--muted)", textAlign: "center" }}>Then share publicly · Google · TripAdvisor</div>
          </div>
        </div>
      </div>
      <div style={{ position: "absolute", right: "-2%", top: "18%", zIndex: 1, transform: "rotate(8deg)" }}>
        <NFCCard size="md" />
      </div>
    </div>
  );
}

function ProofStrip() {
  return (
    <section className="bg-bold" style={{ padding: "120px 0", position: "relative" }}>
      <div className="container">
        <Eyebrow color="rgba(255,255,255,.85)">What actually happens</Eyebrow>
        <h2 className="tt-h tt-h-lg" style={{ color: "var(--bg)", marginTop: 24, maxWidth: 880 }}>The silent majority finally has a way to speak.</h2>
        <div style={{ marginTop: 72, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0, borderTop: "1px solid rgba(255,255,255,.25)" }} className="proof-grid">
          {[
            { v: "+50",     l: "Google reviews",      sub: "in 48 hours · after a decade of operation" },
            { v: "30→100",  l: "reviews per week",    sub: "previous baseline: 30 in twelve months" },
            { v: "<4s",     l: "feedback to floor",   sub: "while the guest is still at the table" }
          ].map((s, i) => (
            <div key={i} style={{ padding: "48px 28px 0", borderRight: i < 2 ? "1px solid rgba(255,255,255,.25)" : 0, color: "var(--bg)", display: "flex", flexDirection: "column" }} className="proof-cell">
              <div style={{ display: "flex", alignItems: "flex-end", minHeight: "clamp(56px, 8.5vw, 132px)" }}>
                <div className="tt-big" style={{ fontSize: "clamp(48px, 7.6vw, 116px)", color: "var(--bg)", letterSpacing: "-0.04em", lineHeight: 1, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
              </div>
              <div style={{ marginTop: 28, fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: 17 }}>{s.l}</div>
              <div style={{ marginTop: 8, fontSize: 14, color: "rgba(255,255,255,.78)", maxWidth: 260, lineHeight: 1.5 }}>{s.sub}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .proof-grid { grid-template-columns: 1fr !important; }
          .proof-cell { border-right: 0 !important; border-bottom: 1px solid rgba(255,255,255,.25); padding: 40px 0 32px !important; }
        }
      `}</style>
    </section>
  );
}

function Mechanism({ go }) {
  const { t } = useTranslation();
  const steps = [
    { n: "01", t: "Tap",    d: "Your team brings the NFC card to the table. The guest taps with any phone." },
    { n: "02", t: "Share",  d: "A clean feedback page. Average completion: 38 seconds." },
    { n: "03", t: "Answer", d: "You see it in real time. The floor manager can act before the bill arrives." },
    { n: "04", t: "Invite", d: "Every guest is invited to share publicly — no matter what they told you privately." }
  ];
  return (
    <section style={{ padding: "120px 0", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)", gap: 64, alignItems: "flex-start" }} className="mech-head">
          <div>
            <Eyebrow>How it works</Eyebrow>
            <h2 className="tt-h tt-h-lg" style={{ marginTop: 24 }}>
              Tap, share, answer, <span style={{ color: "var(--persimmon)" }}>invite.</span>
            </h2>
          </div>
          <p style={{ margin: 0, fontSize: 18, lineHeight: 1.6, color: "var(--ink-2)", maxWidth: 540 }}>
            Every guest takes the same path. There is no branching on sentiment. No filter. No gate. The restaurant's advantage is speed — hearing first, before anyone leaves the restaurant.
          </p>
        </div>
        <div style={{ marginTop: 72, display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 0, borderTop: "1px solid var(--ink)" }} className="mech-grid">
          {steps.map((s, i) => (
            <div key={s.n} style={{ padding: "36px 28px 32px", borderRight: i < 3 ? "1px solid var(--line)" : 0, display: "flex", flexDirection: "column", gap: 24 }} className="mech-cell">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 600, color: "var(--persimmon)", letterSpacing: ".16em" }}>{s.n}</span>
              </div>
              <div className="tt-h tt-h-md" style={{ fontSize: 32 }}>{s.t}</div>
              <p style={{ margin: 0, fontSize: 15, color: "var(--ink-2)", lineHeight: 1.55 }}>{s.d}</p>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 48, display: "flex", justifyContent: "flex-end" }}>
          <a href="#" className="btn btn-link" onClick={(e) => { e.preventDefault(); go("how"); }}>{t("common.seeFullFlow")} →</a>
        </div>
      </div>
      <style>{`
        @media (max-width: 900px) {
          .mech-head { grid-template-columns: 1fr !important; gap: 32px !important; }
          .mech-grid { grid-template-columns: 1fr 1fr !important; }
          .mech-cell:nth-child(2) { border-right: 0 !important; }
          .mech-cell:nth-child(1), .mech-cell:nth-child(2) { border-bottom: 1px solid var(--line); }
        }
      `}</style>
    </section>
  );
}

function CaseRail({ go }) {
  const { t } = useTranslation();
  const cases = [
    { name: "Casa Verde",          city: "Cluj-Napoca", big: "+50",    label: "Google reviews · 48h",     tone: "persimmon", photo: "A guest taps the NFC card after dinner — Casa Verde dining floor." },
    { name: "Bistrot Mărgărit",    city: "Bucharest",   big: "30→100", label: "reviews · per week",       tone: "coral",     photo: "Maître d' brings the bill folder with the card tucked inside." },
    { name: "Hanul Drumețului",    city: "Brașov",      big: "<4s",    label: "feedback to floor",         tone: "persimmon", photo: "F&B director on the floor, phone in hand, reading a live alert." }
  ];
  return (
    <section className="bg-2" style={{ padding: "120px 0", borderBottom: "1px solid var(--line)" }}>
      <div className="container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 24 }}>
          <div>
            <Eyebrow>Real restaurants · real numbers</Eyebrow>
            <h2 className="tt-h tt-h-lg" style={{ marginTop: 24 }}>
              Restaurants are <span style={{ color: "var(--persimmon)" }}>hearing more</span> than ever.
            </h2>
          </div>
          <a href="#" className="btn btn-link" onClick={(e) => { e.preventDefault(); go("results"); }}>{t("common.seeAllResults")} →</a>
        </div>
        <div style={{ marginTop: 64, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 24 }} className="case-grid">
          {cases.map((c, i) => (
            <article key={c.name} style={{ background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 8, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <PhotoFrame ratio="4/3" tone={i === 1 ? "wash" : "ink"} caption={c.photo} hint={c.name + " · " + c.city} style={{ borderRadius: 0 }} />
              <div style={{ padding: "28px", display: "flex", flexDirection: "column", flex: 1 }}>
                <div className="tt-big" style={{ fontSize: "clamp(56px, 5.4vw, 84px)", color: "var(--persimmon)", lineHeight: 0.9, letterSpacing: "-0.04em" }}>{c.big}</div>
                <div style={{ marginTop: 14, fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: 15 }}>{c.label}</div>
                <div style={{ marginTop: "auto", paddingTop: 28, borderTop: "1px solid var(--line)", marginInline: "-28px", marginBottom: "-28px", padding: "24px 28px", display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <div>
                    <div style={{ fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: 15 }}>{c.name}</div>
                    <div style={{ fontSize: 13.5, color: "var(--muted)", marginTop: 2 }}>{c.city}</div>
                  </div>
                  <span style={{ color: "var(--persimmon)" }}><Icon name="arrow-up-right" size={16} /></span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
      <style>{`@media (max-width: 900px){.case-grid{grid-template-columns:1fr !important}}`}</style>
    </section>
  );
}

function PricingTeaser({ go }) {
  const { t } = useTranslation();
  return (
    <section className="bg-2" style={{ padding: "120px 0", borderBottom: "1px solid var(--line)" }}>
      <div className="container" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 64, alignItems: "center" }}>
        <div>
          <Eyebrow>{t("nav.pricing")}</Eyebrow>
          <h2 className="tt-h tt-h-lg" style={{ marginTop: 24 }}>
            {t("pricing.headline1")}<br /><span style={{ color: "var(--persimmon)" }}>{t("pricing.headline2")}</span>
          </h2>
          <p style={{ marginTop: 24, fontSize: 18, lineHeight: 1.6, color: "var(--ink-2)", maxWidth: 480 }}>
            Per restaurant, per month. No setup fees. No per-cover billing. Cancel anytime — pricing transparency is part of Fair Hospitality too.
          </p>
          <div style={{ marginTop: 32 }}>
            <a href="#" className="btn btn-primary" onClick={(e) => { e.preventDefault(); go("pricing"); }}>{t("common.seePlans")} <Icon name="arrow-right" size={16} /></a>
          </div>
        </div>
        <div style={{ background: "var(--bg)", border: "1px solid var(--line)", borderRadius: 8, padding: "40px 36px" }}>
          <div className="tt-big" style={{ fontSize: "clamp(56px, 6vw, 88px)", color: "var(--persimmon)" }}>€79</div>
          <div style={{ marginTop: 14, fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: 17 }}>Starts at, per location, per month</div>
          <div style={{ marginTop: 8, color: "var(--muted)", fontSize: 14.5 }}>Up to a custom House plan for groups of twelve or more.</div>
          <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 12 }}>
            {["Unlimited covers", "Real-time alerts", "Public review invitation, every guest", "Cancel anytime"].map((x) => (
              <div key={x} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 14.5 }}>
                <span style={{ color: "var(--persimmon)" }}><Icon name="check" size={14} stroke={2.2} /></span>
                <span>{x}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 900px) { section.bg-2 .container { grid-template-columns: 1fr !important; } }`}</style>
    </section>
  );
}

function CTA({ go }) {
  const { t } = useTranslation();
  return (
    <section className="bg-bold" style={{ padding: "140px 0", position: "relative", overflow: "hidden" }}>
      <div className="container">
        <h2 className="tt-h tt-h-xl" style={{ color: "var(--bg)", maxWidth: 1000 }}>
          Twenty minutes with us.<br />
          <span style={{ opacity: .85 }}>A different restaurant tomorrow.</span>
        </h2>
        <div style={{ marginTop: 48, display: "flex", gap: 14, flexWrap: "wrap" }}>
          <a href="#" className="btn btn-lg" onClick={(e) => { e.preventDefault(); go("contact"); }} style={{ background: "var(--bg)", color: "var(--ink)" }}>
            {t("common.bookDemo")} <Icon name="arrow-right" size={16} />
          </a>
          <a href="#" className="btn btn-lg" onClick={(e) => { e.preventDefault(); go("results"); }} style={{ background: "transparent", color: "var(--bg)", border: "1px solid rgba(255,255,255,.6)" }}>
            See real results
          </a>
        </div>
      </div>
    </section>
  );
}
